import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, MapPin, Clock } from 'lucide-react';
import BlogEmailCapture from '../components/BlogEmailCapture';
import MidtownStudioCallout from '../components/MidtownStudioCallout';

const BlogPostWhatToWear = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'What Should I Wear to My First Massage? | Ayumi Suzuki';
    return () => { document.title = prev; };
  }, []);

  const isoDate = '2026-02-13';
  const canonical = 'https://ayumisuzuki.com/blog/what-should-i-wear-to-my-first-massage';

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="what-to-wear-blog-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'What Should I Wear to My First Massage? Real Answers From a Sacramento Therapist',
            description:
              'What should you wear to your first massage? A warm, honest answer from certified midtown Sacramento therapist Ayumi Suzuki — what to wear to the studio, what to wear on the table, and how to feel comfortable throughout.',
            image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1600&auto=format&fit=crop&q=80',
            author: { '@type': 'Person', name: 'Ayumi Suzuki', url: 'https://ayumisuzuki.com/about', jobTitle: 'Certified Massage Therapist' },
            publisher: {
              '@type': 'Organization',
              name: 'Ayumi Suzuki Massage Therapy',
              logo: { '@type': 'ImageObject', url: 'https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/hdysr3f5_ayumi1%20logo.jpg' },
            },
            datePublished: isoDate,
            dateModified: isoDate,
            mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
            articleSection: 'Personal Notes',
            keywords: 'what should I wear to my first massage, what to wear massage, first massage what to expect, Ayumi Suzuki',
            about: [{ '@type': 'Thing', name: 'First Massage Experience' }, { '@type': 'Place', name: 'Sacramento, California' }],
            inLanguage: 'en-US',
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'QAPage',
            mainEntity: {
              '@type': 'Question',
              name: 'What should I wear to my first massage?',
              text: 'What should I wear to a massage appointment as a first-timer, and what happens on the table?',
              answerCount: 1,
              author: { '@type': 'Person', name: 'Ayumi Suzuki' },
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Wear whatever is comfortable to and from the studio — loose, easy-to-change clothes are ideal. On the table, most clients undress to their comfort level (many go fully undressed under the sheet; some keep underwear on; a few stay clothed). You are always covered by a warm sheet — I only work on the area I\'m currently on. Your comfort dictates everything. At Ayumi Suzuki\'s midtown Sacramento studio (2528 I Street), we\'ll walk through it together before the session starts.',
                author: { '@type': 'Person', name: 'Ayumi Suzuki', jobTitle: 'Certified Massage Therapist' },
                dateCreated: isoDate,
                upvoteCount: 0,
                url: canonical,
              },
            },
          }),
        }}
      />

      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group mb-4" data-testid="back-to-blog-link">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Blog</span>
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="mb-12">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
            <span className="flex items-center" data-testid="post-date"><Calendar className="w-4 h-4 mr-2" /><time dateTime={isoDate}>February 13, 2026</time></span>
            <span className="flex items-center"><User className="w-4 h-4 mr-2" />Ayumi Suzuki</span>
            <span className="flex items-center"><MapPin className="w-4 h-4 mr-2" />Midtown Sacramento</span>
            <span className="flex items-center"><Clock className="w-4 h-4 mr-2" />5 min read</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-gray-800 mb-6 leading-tight" data-testid="post-title">
            What Should I Wear to My First Massage?
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            One of the most-asked questions I get from first-timers — and one nobody should feel weird for asking. Here's what to wear, what to expect, and how to feel comfortable through the whole thing.
          </p>
        </header>

        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
          <img src="https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1600&auto=format&fit=crop&q=80" alt="Welcoming spa setup for a first massage appointment in midtown Sacramento" className="w-full h-96 object-cover" />
        </div>

        <div className="bg-white border border-teal-100 rounded-2xl p-6 mb-12 shadow-sm">
          <h2 className="text-xl font-serif text-teal-700 mb-2">The Short Answer</h2>
          <p className="text-gray-700 leading-relaxed">
            Wear whatever's comfortable to get to and from the studio — loose is ideal. On the table, undress to <em>your</em> comfort level. You're always covered by a warm sheet, and only the area being worked on is uncovered. Nothing about this is on autopilot — I walk you through it. Come in at <strong>2528 I Street, midtown Sacramento</strong>. Text me at <a href="sms:+14156789133" className="text-teal-600 hover:underline">415-678-9133</a> with any questions.
          </p>
        </div>

        <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-a:text-teal-700">
          <h2>What to Wear <em>to</em> the Studio</h2>
          <p>
            Loose, easy-to-change clothing is your friend. Something like:
          </p>
          <ul>
            <li>Sweatpants or joggers, or an easy dress</li>
            <li>A t-shirt or soft top</li>
            <li>Slip-on shoes if possible</li>
            <li>Layers you can peel off — you'll feel warmer after the session</li>
          </ul>
          <p>
            Skip anything you'll need help getting back into. You'll feel a little floaty afterward and buttons and belts will feel like too much.
          </p>

          <h2>What to Wear <em>on</em> the Table</h2>
          <p>
            This is the part people get anxious about — and it doesn't need to be. Here are your options, and every one is normal:
          </p>
          <ul>
            <li><strong>Fully undressed under the sheet.</strong> Most clients choose this — it allows the therapist to work on the full body with oil.</li>
            <li><strong>Underwear only.</strong> Also very common. Works fine for almost every session.</li>
            <li><strong>Fully clothed.</strong> Some clients prefer this, especially their first session. Loose, stretchy clothing works best.</li>
          </ul>
          <p>
            Whatever you choose, I only see whatever <em>you</em> uncover. You go in, get on the table, and pull the sheet up. When I come in, you're already draped. I only expose the area I'm currently working on — a leg, an arm, one side of the back — and it's covered again the second I move to the next area.
          </p>

          <h2>The One Thing I Wish More First-Timers Knew</h2>
          <p>
            It's not weird to feel a little nervous. Almost every first-time client I've worked with has said some version of "I don't really know how this is supposed to go." That's normal. My job is to make the whole thing feel easy — the intake, the draping, the transitions. You don't have to know anything. Show up, and I'll walk you through it.
          </p>

          <h2>Other Things Worth Knowing</h2>
          <ul>
            <li><strong>Hydrate before.</strong> A glass or two of water in the hour before your session.</li>
            <li><strong>Skip a heavy meal.</strong> Light snack an hour before is ideal.</li>
            <li><strong>Arrive a few minutes early.</strong> Not to rush — just to settle in.</li>
            <li><strong>Skin products.</strong> Lotion or perfume before is fine; skip freshly applied self-tanner (it comes off with oil).</li>
            <li><strong>Jewelry.</strong> Best to leave rings, necklaces, and earrings at home or in your bag.</li>
          </ul>

          <h2>A Few More Honest Answers</h2>

          <h3>Can I keep my socks on if my feet get cold?</h3>
          <p>Yes. Say so at intake and we'll skip feet, or work them last so you can put socks back on right after.</p>

          <h3>Will I be exposed at any point?</h3>
          <p>No. Draping is continuous and non-negotiable. You are covered from the moment I come in the room to the moment I leave.</p>

          <h3>What if I don't want a certain area touched?</h3>
          <p>Tell me at intake. Any area is off-limits with a word. This is your session.</p>

          <h3>Do I have to talk during the massage?</h3>
          <p>Nope. Silence is welcome. Most sessions are quiet.</p>

          {/* CTA */}
          <div className="not-prose bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white mt-12">
            <h3 className="text-2xl font-serif mb-4">Come In for Your First One</h3>
            <p className="mb-6">Midtown Sacramento, 2528 I Street. I promise the wearing-clothes part is the least important thing about it — the goal is for you to leave feeling more like yourself.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book" className="inline-block bg-white text-teal-700 px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-300 text-center" data-testid="cta-book-now">Book a Session</Link>
              <Link to="/about" className="inline-block bg-teal-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-teal-900 transition-colors duration-300 text-center" data-testid="cta-about">A Little About Me</Link>
            </div>
          </div>
        </div>

        <MidtownStudioCallout />
        <BlogEmailCapture source="what-to-wear" />

        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-start space-x-6">
            <img src="https://customer-assets.emergentagent.com/job_suzuki-2026-refresh/artifacts/aautielc_Ayumi.jpg" alt="Ayumi Suzuki" className="w-24 h-24 rounded-full object-cover" />
            <div>
              <h3 className="text-2xl font-serif text-gray-800 mb-2">— Ayumi</h3>
              <p className="text-gray-600 mb-4">
                California CAMTC-certified. 2528 I Street, midtown Sacramento. If you've never had a massage, come try one with me. I promise the first-timer stuff is the easy part.
              </p>
              <Link to="/about" className="text-teal-600 hover:text-teal-700 font-medium">Read my longer story →</Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostWhatToWear;
